import { RefObject, useEffect, useState } from 'react'
import { Modal } from 'react-daisyui'
import { IconDownload, IconInfoCircle } from '@tabler/icons-react'
import { Button } from '@/components/Button.tsx'
import { DownloadOptions } from '@/models/downloadOptions.ts'

/**
 * The three modes a photographer can pick, in the order they are shown.
 * `''` (all resolutions) is the default and stays first.
 */
export const DOWNLOAD_OPTIONS_CHOICES: Array<{ value: DownloadOptions, label: string, description: string }> = [
  {
    value: '',
    label: 'Alle Auflösungen',
    description: 'Kunden wählen beim Download zwischen hoher Auflösung und Originaldateien.',
  },
  {
    value: 'detail',
    label: 'Nur hohe Auflösung',
    description: 'Web-optimierte Version: kleinere Dateien, maximale Kompatibilität. Originale werden nie herausgegeben.',
  },
  {
    value: 'original',
    label: 'Nur Originaldateien',
    description: 'Volle Auflösung, genau so wie du sie hochgeladen hast.',
  },
]

/** Short label for a value — used for the summary next to the gear icon. */
export function downloadOptionsLabel(value?: DownloadOptions | null): string {
  const choice = DOWNLOAD_OPTIONS_CHOICES.find(c => c.value === (value || ''))
  return (choice ?? DOWNLOAD_OPTIONS_CHOICES[0]).label
}

interface DownloadOptionsModalProps {
  modalRef: RefObject<HTMLDialogElement>
  value: DownloadOptions
  /** A watermark overrides the setting — shown as a hint, the choice stays editable. */
  hasWatermark: boolean
  isSaving?: boolean
  /** Called only when the user confirms with "Speichern". */
  onSave: (value: DownloadOptions) => void
}

/**
 * Picks which download resolutions the gallery offers. Unlike the instantly-saving
 * settings around it this one is a draft: picking an option changes nothing until
 * "Speichern", and "Abbrechen" throws the draft away.
 */
export default function DownloadOptionsModal({
  modalRef,
  value,
  hasWatermark,
  isSaving,
  onSave,
}: DownloadOptionsModalProps) {
  const [draft, setDraft] = useState<DownloadOptions>(value || '')

  // Adopt the saved value again whenever it changes (after a save landed) and whenever the
  // dialog is closed, so a cancelled draft never survives into the next opening.
  useEffect(() => {
    setDraft(value || '')
  }, [value])

  useEffect(() => {
    const dialog = modalRef.current
    if (!dialog) return
    const reset = () => setDraft(value || '')
    dialog.addEventListener('close', reset)
    return () => dialog.removeEventListener('close', reset)
  }, [modalRef, value])

  const isDirty = draft !== (value || '')

  function handleCancel() {
    setDraft(value || '')
    modalRef.current?.close()
  }

  return (
    <Modal ref={modalRef} className="bg-neutral">
      <Modal.Header>
        <div className="flex flex-row gap-2 justify-center">
          <div className="bg-primary text-neutral rounded-full p-1">
            <IconDownload size={20} />
          </div>
          Download-Optionen
        </div>
        <button
          type="button"
          className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5"
          disabled={isSaving}
          onClick={handleCancel}
        >✕</button>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-3">
          <p className="text-sm opacity-70">
            Welche Auflösungen deine Kunden in der Galerie herunterladen können.
          </p>

          {hasWatermark && (
            <div className="flex gap-2 items-start p-3 bg-secondary border border-[var(--portal-border)] text-sm">
              <IconInfoCircle size={18} className="shrink-0 mt-0.5 opacity-60" />
              <span>
                Solange ein Wasserzeichen aktiv ist, wird immer nur die hohe Auflösung mit Wasserzeichen
                angeboten. Deine Einstellung hier greift wieder, sobald du das Wasserzeichen entfernst.
              </span>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {DOWNLOAD_OPTIONS_CHOICES.map(choice => {
              const isSelected = draft === choice.value
              return (
                <label
                  key={choice.value || 'all'}
                  className={`flex gap-3 items-start p-3 border cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-secondary'
                      : 'border-[var(--portal-border)] hover:bg-secondary'
                  }`}
                >
                  <input
                    type="radio"
                    name="download-options"
                    className="radio radio-primary radio-sm mt-0.5"
                    checked={isSelected}
                    disabled={isSaving}
                    onChange={() => setDraft(choice.value)}
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="font-medium">{choice.label}</span>
                    <span className="text-sm opacity-60">{choice.description}</span>
                  </span>
                </label>
              )
            })}
          </div>

          <div className="flex items-center justify-end gap-2 mt-2">
            <Button size="sm" color="ghost" disabled={isSaving} onClick={handleCancel}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              color="primary"
              loading={isSaving}
              disabled={isSaving || !isDirty}
              onClick={() => onSave(draft)}
            >
              Speichern
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
